<template lang="html">
<div class="af-generator" :class="{ 'af-generator-embedded': embedded }">
    <div v-if="loading" class="af-loading">Loading...</div>

    <template v-else>
        <div v-if="!embedded" class="af-header">
            <h1>AppsFlyer Link Generator</h1>
            <p>Only partners with all four sub fields configured appear here. ONO-enabled accounts are listed below; others are under See more.</p>
        </div>

        <div class="af-card">
            <div class="af-grid-2">
                <div>
                    <label class="af-label">Link type</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: linkType === 'cta' }" @click="setLinkType('cta')">CTA only</button>
                        <button type="button" class="af-seg" :class="{ active: linkType === 'cta_vta' }" @click="setLinkType('cta_vta')">CTA + VTA</button>
                    </div>
                </div>
                <div>
                    <label class="af-label">OS</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: os === 'ios' }" @click="os = 'ios'">iOS</button>
                        <button type="button" class="af-seg" :class="{ active: os === 'android' }" @click="os = 'android'">Android</button>
                    </div>
                </div>
            </div>

            <div class="af-field">
                <label class="af-label">Ad network / AF account — pid (multi-select)</label>
                <div class="af-pills">
                    <button
                        v-for="n in networks"
                        :key="n.pid"
                        type="button"
                        class="af-pill"
                        :class="{ active: selected.includes(n.pid) }"
                        :title="networkPillTitle(n)"
                        @click="toggleNet(n.pid)"
                    >
                        {{ n.name }} <span class="af-pill-pid">({{ n.pid }})</span>
                    </button>
                </div>

                <template v-if="showMore">
                    <div class="af-subsection-label">PIDs without ONO — not available in main list</div>
                    <div class="af-pills">
                        <button
                            v-for="n in otherNetworks"
                            :key="n.pid"
                            type="button"
                            class="af-pill af-pill-other"
                            :class="{ active: selected.includes(n.pid) }"
                            :title="networkPillTitle(n)"
                            @click="toggleNet(n.pid)"
                        >
                            {{ n.name }} <span class="af-pill-pid">({{ n.pid }})</span>
                        </button>
                    </div>
                </template>

                <button
                    v-if="otherNetworks.length"
                    type="button"
                    class="af-toggle-more"
                    @click="showMore = !showMore"
                >
                    {{ showMore ? 'Show fewer' : `See more (${otherNetworks.length} other networks)` }}
                </button>
                <p class="af-hint">{{ selected.length }} selected. All listed partners use their configured sub values.</p>
            </div>

            <div class="af-field">
                <label class="af-label">Bundle ID</label>
                <div class="af-combo" ref="bundleBox" :class="{ 'is-open': showBundleDropdown }">
                    <div class="af-combo-input-wrap">
                        <input
                            :value="bundleQuery"
                            type="text"
                            class="af-input"
                            :class="{ 'af-input-error': bundleError }"
                            :placeholder="os === 'ios' ? 'Type App Store ID or app name…' : 'Type package name or app name…'"
                            @focus="bundleOpen = true"
                            @input="handleBundleInput"
                        />
                        <button
                            v-if="bundleQuery"
                            type="button"
                            class="af-combo-clear"
                            aria-label="Clear"
                            @click="clearBundle"
                        >×</button>
                        <button
                            type="button"
                            class="af-combo-chevron"
                            :disabled="!!bundleQuery.trim() || !!selectedBundle"
                            :title="bundleQuery.trim() || selectedBundle ? 'Clear the field to browse all' : 'Browse all bundles'"
                            aria-label="Browse all bundles"
                            @click="toggleBundleFullList"
                        >{{ showBundleDropdown ? '▴' : '▾' }}</button>
                    </div>
                    <ul v-if="showBundleDropdown" class="af-combo-dropdown">
                        <li v-if="bundleSearching && bundleMatches.length === 0" class="af-combo-status">Searching…</li>
                        <li v-for="b in bundleMatches" :key="'b-' + b.bundle_id">
                            <button type="button" :class="{ active: bundleId === b.bundle_id }" @click.prevent="pickBundle(b)">
                                <span>
                                    <span class="af-combo-app">{{ b.app_name }}</span>
                                    <span class="af-combo-id">{{ b.bundle_id }}</span>
                                </span>
                                <span class="af-combo-meta">{{ b.os }}{{ b.vertical_name ? ' · ' + b.vertical_name : '' }}</span>
                            </button>
                        </li>
                        <li v-if="!bundleSearching && bundleTouched && bundleQuery.trim() && bundleMatches.length === 0" class="af-combo-status">
                            No bundle matches "{{ bundleQuery.trim() }}"
                        </li>
                        <li v-if="!bundleSearching && !bundleQuery.trim() && bundleMatches.length === 0 && bundleBrowsingAll" class="af-combo-status">
                            No bundles found
                        </li>
                    </ul>
                </div>
                <p v-if="bundleError" class="af-error">{{ bundleError }}</p>
                <p v-else-if="selectedBundle" class="af-hint">
                    {{ selectedBundle.app_name || selectedBundle.app || 'Selected' }}
                    · {{ selectedBundle.bundle_id }}
                    <template v-if="selectedBundle.os"> · {{ selectedBundle.os }}</template>
                </p>
                <p v-else class="af-hint">
                    {{ os === 'ios' ? 'URL → app.appsflyer.com/id&lt;your id&gt;' : 'URL → app.appsflyer.com/&lt;package&gt; (no "id" prefix)' }}
                </p>
            </div>

            <div class="af-field">
                <label class="af-label">Agency — af_prt (optional)</label>
                <div class="af-agency-row">
                    <select v-model="agencyChoice" class="af-input af-agency-select" @change="emitFormSync">
                        <option value="none">none</option>
                        <option v-for="a in agenciesForSelect" :key="a.pid" :value="a.pid">{{ a.name }}</option>
                        <option value="Other">Other</option>
                    </select>
                    <input
                        v-if="isOtherAgency"
                        :value="agencyOther"
                        type="text"
                        class="af-input"
                        placeholder="Agency name"
                        @input="onAgencyOtherInput($event.target.value)"
                    />
                </div>
            </div>

            <div class="af-grid-3">
                <div>
                    <label class="af-label">Campaign name — c</label>
                    <input v-model="campaignName" type="text" class="af-input" placeholder="summer_promo" />
                </div>
                <div>
                    <label class="af-label">Cost model — af_cost_model</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: costModel === 'cpi' }" @click="setCostModel('cpi')">CPI</button>
                        <button type="button" class="af-seg" :class="{ active: costModel === 'cpa' }" @click="setCostModel('cpa')">CPA</button>
                    </div>
                </div>
                <div>
                    <label class="af-label">Is retargeting?</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: isRetargeting === 'no' }" @click="isRetargeting = 'no'">No</button>
                        <button type="button" class="af-seg" :class="{ active: isRetargeting === 'yes' }" @click="isRetargeting = 'yes'">Yes</button>
                    </div>
                </div>
            </div>

            <div class="af-field">
                <label class="af-label">Additional parameters (optional)</label>
                <input
                    v-model="additionalParams"
                    type="text"
                    class="af-input af-input-mono"
                    placeholder="af_keywords=foo&custom_x=bar"
                />
                <p class="af-hint">Appended raw to the end. Leading "&amp;" or "?" is stripped.</p>
            </div>

            <div v-if="isRetargeting === 'yes'" class="af-grid-2 af-retarget-windows">
                <div class="af-field">
                    <label class="af-label">af_reengagement_window</label>
                    <input
                        v-model="reengagementWindow"
                        type="text"
                        class="af-input af-input-mono"
                        placeholder="30d"
                    />
                </div>
                <div class="af-field">
                    <label class="af-label">af_inactivity_window</label>
                    <input
                        v-model="inactivityWindow"
                        type="text"
                        class="af-input af-input-mono"
                        placeholder="10d"
                    />
                </div>
            </div>

            <div class="af-advanced">
                <div class="af-advanced-header">
                    <button type="button" class="af-advanced-toggle" @click="advancedOpen = !advancedOpen">
                        {{ advancedOpen ? '▾' : '▸' }} Advanced — edit any parameter
                    </button>
                    <button
                        v-if="advancedOpen"
                        type="button"
                        class="af-advanced-reset"
                        @click="resetAdvanced"
                    >
                        Reset
                    </button>
                </div>
                <div v-show="advancedOpen" class="af-advanced-body">
                    <p class="af-hint">Clear a field to omit it from the URL. Blank af_channel keeps random-per-link.</p>
                    <div class="af-advanced-grid">
                        <div v-for="field in advancedFields" :key="field.key" class="af-field">
                            <label class="af-label">{{ field.label }}</label>
                            <input
                                v-model="advanced[field.key]"
                                type="text"
                                class="af-input af-input-mono"
                                :placeholder="field.placeholder"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="error" class="af-error af-error-block">{{ error }}</p>

            <button type="button" class="af-generate-btn" @click="generate">Generate links</button>
        </div>

        <div v-if="results" class="af-results">
            <div class="af-results-header">
                <h2>Generated — {{ results.length }} account{{ results.length > 1 ? 's' : '' }}</h2>
                <div class="af-results-actions">
                    <button type="button" class="af-copy-btn" @click="generate">Re-roll</button>
                    <button type="button" class="af-copy-btn" @click="copyText(allText, 'all')">
                        {{ copiedKey === 'all' ? 'Copied ✓' : 'Copy all' }}
                    </button>
                </div>
            </div>

            <div v-for="r in results" :key="r.pid" class="af-result-card">
                <div class="af-result-meta">
                    <span class="af-result-name">{{ r.name }}</span>
                    <span class="af-badge">pid={{ r.pid }}</span>
                    <span class="af-badge">af_channel={{ r.channel }}</span>
                    <template v-if="r.subFields">
                        <span class="af-badge">af_adset={{ r.subFields.af_adset }}</span>
                        <span class="af-badge">af_adset_id={{ r.subFields.af_adset_id }}</span>
                        <span class="af-badge">af_ad={{ r.subFields.af_ad }}</span>
                        <span class="af-badge">af_ad_id={{ r.subFields.af_ad_id }}</span>
                    </template>
                    <template v-else>
                        <span class="af-badge">adset={{ r.adset }}</span>
                        <span class="af-badge">ad={{ r.ad }}</span>
                    </template>
                </div>

                <div class="af-link-block">
                    <div class="af-link-label">CTA</div>
                    <div class="af-link-row">
                        <textarea
                            v-if="isEditingUrl(r.pid + '-cta')"
                            class="af-link-code af-link-code-edit"
                            :value="r.cta"
                            spellcheck="false"
                            @input="onEditedUrl(r, 'cta', $event.target.value)"
                        ></textarea>
                        <code v-else class="af-link-code">{{ r.cta }}</code>
                        <div class="af-link-actions">
                            <button type="button" class="af-copy-btn" @click="toggleEditUrl(r.pid + '-cta')">
                                {{ isEditingUrl(r.pid + '-cta') ? 'Done' : 'Edit' }}
                            </button>
                            <button type="button" class="af-copy-btn" @click="copyText(r.cta, r.pid + '-cta')">
                                {{ copiedKey === r.pid + '-cta' ? 'Copied ✓' : 'Copy' }}
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="r.vta" class="af-link-block">
                    <div class="af-link-label">VTA</div>
                    <div class="af-link-row">
                        <textarea
                            v-if="isEditingUrl(r.pid + '-vta')"
                            class="af-link-code af-link-code-edit"
                            :value="r.vta"
                            spellcheck="false"
                            @input="onEditedUrl(r, 'vta', $event.target.value)"
                        ></textarea>
                        <code v-else class="af-link-code">{{ r.vta }}</code>
                        <div class="af-link-actions">
                            <button type="button" class="af-copy-btn" @click="toggleEditUrl(r.pid + '-vta')">
                                {{ isEditingUrl(r.pid + '-vta') ? 'Done' : 'Edit' }}
                            </button>
                            <button type="button" class="af-copy-btn" @click="copyText(r.vta, r.pid + '-vta')">
                                {{ copiedKey === r.pid + '-vta' ? 'Copied ✓' : 'Copy' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
</div>
</template>

<script>
import {
    buildUrl,
    validateBundle,
    pickChannel,
    buildAdsetAd,
    partnerSubFields,
    DEFAULT_ADVANCED,
    DEFAULT_RETARGETING,
    stripWhitespaceIfPresent,
    isOtherAgency,
    formatBundleInputLabel,
} from './appsflyer-link-utils'

const ADVANCED_FIELDS = [
    { key: 'af_click_lookback', label: 'af_click_lookback', placeholder: '7d' },
    { key: 'af_viewthrough_lookback', label: 'af_viewthrough_lookback', placeholder: '24h' },
    { key: 'af_sub_siteid', label: 'af_sub_siteid', placeholder: '{sub15}{sub6}{sub20}' },
    { key: 'af_ad_type', label: 'af_ad_type', placeholder: 'banner' },
    { key: 'af_media_type', label: 'af_media_type', placeholder: 'app' },
    { key: 'af_model', label: 'af_model', placeholder: '{device_model}' },
    { key: 'af_cost_currency', label: 'af_cost_currency', placeholder: 'usd' },
    { key: 'af_ua', label: 'af_ua', placeholder: '{device_ua}' },
    { key: 'af_ip', label: 'af_ip', placeholder: '{ip}' },
    { key: 'device_id', label: 'idfa / advertising_id', placeholder: '{sub5}' },
    { key: 'af_os_version', label: 'af_os_version', placeholder: '{os_version}' },
    { key: 'af_language', label: 'af_language', placeholder: '{language}' },
    { key: 'af_ad_format', label: 'af_ad_format', placeholder: 'image' },
    { key: 'af_channel', label: 'af_channel (blank = random)', placeholder: 'leave blank for random' },
]

export default {
    name: 'AppsflyerLinkGeneratorCore',
    props: {
        embedded: {
            type: Boolean,
            default: false,
        },
        initial: {
            type: Object,
            default: null,
        },
        autoGenerate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            linkType: 'cta',
            os: 'ios',
            bundleId: '',
            bundleQuery: '',
            bundleOpen: false,
            bundleTouched: false,
            bundleMatches: [],
            bundleSearching: false,
            bundleBrowsingAll: false,
            selectedBundleCache: null,
            agencyChoice: 'none',
            agencyOther: '',
            selected: [],
            showMore: false,
            campaignName: '',
            originOverride: '',
            costModel: 'cpi',
            isRetargeting: 'no',
            reengagementWindow: DEFAULT_RETARGETING.af_reengagement_window,
            inactivityWindow: DEFAULT_RETARGETING.af_inactivity_window,
            additionalParams: '',
            advancedOpen: false,
            advanced: { ...DEFAULT_ADVANCED },
            advancedFields: ADVANCED_FIELDS,
            results: null,
            error: '',
            networks: [],
            otherNetworks: [],
            agencies: [],
            reserveCodes: [],
            copiedKey: null,
            copiedTimeout: null,
            editingUrls: {},
        }
    },
    computed: {
        canApply() {
            return Array.isArray(this.results) && this.results.length > 0
        },
        selectedBundle() {
            if (this.selectedBundleCache && this.selectedBundleCache.bundle_id === this.bundleId) {
                return this.selectedBundleCache
            }
            return this.bundleMatches.find((b) => b.bundle_id === this.bundleId) || null
        },
        showBundleDropdown() {
            return (
                this.bundleOpen &&
                !this.selectedBundle &&
                (this.bundleSearching || this.bundleMatches.length > 0 || this.bundleQuery.trim().length > 0 || this.bundleBrowsingAll)
            )
        },
        isOtherAgency() {
            return isOtherAgency(this.agencyChoice)
        },
        resolvedAgency() {
            if (this.isOtherAgency) return String(this.agencyOther || '').trim()
            if (!this.agencyChoice || this.agencyChoice === 'none') return ''
            return String(this.agencyChoice).trim()
        },
        agenciesForSelect() {
            return (this.agencies || []).filter((a) => !isOtherAgency(a.name) && !isOtherAgency(a.pid))
        },
        bundleError() {
            const id = this.normalizeBundleId()
            return id ? validateBundle(this.os, id) : ''
        },
        withVTA() {
            return this.linkType === 'cta_vta'
        },
        allNetworks() {
            return [...this.networks, ...this.otherNetworks]
        },
        allText() {
            if (!this.results) return ''
            return this.results
                .flatMap((r) => {
                    const subLine = r.subFields
                        ? `af_adset=${r.subFields.af_adset} af_adset_id=${r.subFields.af_adset_id} af_ad=${r.subFields.af_ad} af_ad_id=${r.subFields.af_ad_id}`
                        : `adset=${r.adset} ad=${r.ad}`
                    return [
                        `# ${r.name} (${r.pid}) | af_channel=${r.channel} ${subLine}`,
                        `CTA: ${r.cta}`,
                        ...(r.vta ? [`VTA: ${r.vta}`] : []),
                        '',
                    ]
                })
                .join('\n')
        },
    },
    async created() {
        this._bundleSearchSeq = 0
        this._bundleTimer = null
        document.addEventListener('mousedown', this.onDocClick)
        this.applyInitial(this.initial)
        await this.getdata()
        this.applyInitial(this.initial)
        if (this.bundleId) await this.hydrateBundle(this.bundleId)
        this.loading = false
        this._syncReady = true
        if (this.autoGenerate) {
            this.$nextTick(() => this.generate())
        }
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocClick)
        if (this._bundleTimer) clearTimeout(this._bundleTimer)
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
    },
    methods: {
        normalizeVerbalIds(verbalIds) {
            if (typeof verbalIds === 'string') {
                return verbalIds
                    .split(',')
                    .map((id) => parseInt(id.trim(), 10))
                    .filter((id) => !isNaN(id))
            }
            if (!Array.isArray(verbalIds)) {
                return []
            }
            return verbalIds
                .map((id) => parseInt(id, 10))
                .filter((id) => !isNaN(id))
        },
        isOnoPartner(obj) {
            return obj && (obj.ono === 1 || obj.ono === true)
        },
        async getdata() {
            try {
                const verbalsData = await this.$http.post(this.resources.Management.getDmVerbals, {})
                const pidData = await this.$http.post(this.resources.Management.getPid, {})

                const verbalRows = verbalsData.body.data || []
                const verbalCodeById = {}
                const generalCodes = []
                verbalRows.forEach((row) => {
                    verbalCodeById[Number(row.id)] = row.code
                    if (row.is_general === 1 || row.is_general === true) {
                        generalCodes.push(row.code)
                    }
                })
                this.reserveCodes = generalCodes

                const pidRows = (pidData.body.data || []).filter((obj) => obj.pid_text !== 'Other')
                const onoNetworks = []
                const otherNetworks = []
                pidRows.forEach((obj) => {
                    const subFields = partnerSubFields(obj)
                    if (!subFields) {
                        return
                    }
                    const verbalIds = this.normalizeVerbalIds(obj.verbal_ids)
                    const codes = verbalIds
                        .map((id) => verbalCodeById[id])
                        .filter(Boolean)
                    const network = {
                        name: obj.pid_text,
                        pid: obj.pid_value,
                        codes: codes.length ? codes : this.reserveCodes,
                        subFields,
                        ono: this.isOnoPartner(obj),
                    }
                    if (this.isOnoPartner(obj)) {
                        onoNetworks.push(network)
                    } else {
                        otherNetworks.push(network)
                    }
                })

                this.networks = onoNetworks
                this.otherNetworks = otherNetworks
                const all = [...onoNetworks, ...otherNetworks]
                this.selected = this.selected.filter((pid) => all.some((n) => n.pid === pid))
                if (this.selected.some((pid) => otherNetworks.some((n) => n.pid === pid))) {
                    this.showMore = true
                }

                const afPartData = await this.$http.post(this.resources.Management.getAfPart, {})
                this.agencies = (afPartData.body.data || []).map((obj) => ({
                    name: obj.af_part_text,
                    pid: obj.af_part_value,
                }))
            } catch (err) {
                console.log(err)
                this.notifyError('Failed to load generator data')
            }
        },
        applyInitial(initial) {
            if (!initial || typeof initial !== 'object') return
            if (initial.linkType === 'cta' || initial.linkType === 'cta_vta') {
                this.linkType = initial.linkType
            }
            if (initial.os === 'ios' || initial.os === 'android') {
                this.os = initial.os
            }
            if (initial.bundleId != null && initial.bundleId !== '') {
                this.bundleId = String(initial.bundleId)
                if (!this.selectedBundle) this.bundleQuery = this.bundleId
            }
            if (initial.agency != null) {
                this.applyAgencyValue(initial.agency)
            }
            if (Array.isArray(initial.selectedPids)) {
                this.selected = initial.selectedPids.map(String).filter(Boolean)
            }
            if (initial.campaignName != null) {
                this.campaignName = String(initial.campaignName)
            }
            if (initial.originOverride != null) {
                this.originOverride = String(initial.originOverride)
            }
            if (initial.costModel === 'cpi' || initial.costModel === 'cpa') {
                this.costModel = initial.costModel
            }
        },
        applyAgencyValue(raw) {
            const val = raw === 'none' ? '' : String(raw == null ? '' : raw).trim()
            if (!val) {
                this.agencyChoice = 'none'
                this.agencyOther = ''
                return
            }
            const hit = this.agencies.find((a) => a.pid === val || a.name === val)
            if (hit) {
                this.agencyChoice = hit.pid
                this.agencyOther = ''
                return
            }
            this.agencyChoice = 'Other'
            this.agencyOther = isOtherAgency(val) ? '' : val
        },
        onDocClick(e) {
            if (this.$refs.bundleBox && !this.$refs.bundleBox.contains(e.target)) {
                this.bundleOpen = false
            }
        },
        onAgencyOtherInput(value) {
            const next = stripWhitespaceIfPresent(value)
            if (next.stripped) this.notifyError('Agency name should not contain white spaces')
            this.agencyOther = next.value
            this.emitFormSync()
        },
        setLinkType(type) {
            this.linkType = type
            this.emitFormSync()
        },
        setCostModel(model) {
            this.costModel = model
            this.emitFormSync()
        },
        emitFormSync() {
            if (!this.embedded || this.loading || !this._syncReady) return
            this.$emit('sync', {
                linkType: this.linkType,
                os: this.os,
                bundleId: this.bundleId,
                bundle: this.selectedBundleCache || this.selectedBundle || null,
                agencyChoice: this.agencyChoice,
                agencyOther: this.agencyOther,
                selectedPids: this.selected.slice(),
                costModel: this.costModel,
            })
        },
        applyBundleOs(b) {
            const osRaw = String((b && b.os) || '').toLowerCase()
            if (osRaw === 'ios') this.os = 'ios'
            else if (osRaw === 'android') this.os = 'android'
        },
        handleBundleInput(e) {
            this.bundleQuery = e.target.value
            this.bundleTouched = true
            this.bundleBrowsingAll = false
            this.bundleOpen = true
            if (this.bundleId) {
                this.bundleId = ''
                this.selectedBundleCache = null
            }
            if (this._bundleTimer) clearTimeout(this._bundleTimer)
            this._bundleSearchSeq += 1
            const q = this.bundleQuery.trim()
            if (!q) {
                this.bundleSearching = false
                this.bundleMatches = []
                return
            }
            this.bundleSearching = true
            this.bundleMatches = []
            const seq = this._bundleSearchSeq
            this._bundleTimer = setTimeout(() => {
                this.runBundleSearch(q, seq)
            }, 300)
        },
        async runBundleSearch(q, seq) {
            if (seq !== this._bundleSearchSeq) return
            this.bundleSearching = true
            this.bundleOpen = true
            try {
                const res = await this.$http.post(this.resources.BundleIDs.searchBundles, { q })
                if (seq !== this._bundleSearchSeq) return
                const rows = (res && res.body && res.body.data) || []
                this.bundleMatches = rows
                const exact = rows.find((b) => b.bundle_id === q)
                if (exact) this.pickBundle(exact)
                else if (rows.length === 1) this.pickBundle(rows[0])
            } catch (err) {
                console.error('searchBundles', err)
                if (seq !== this._bundleSearchSeq) return
                this.bundleMatches = []
            } finally {
                if (seq === this._bundleSearchSeq) {
                    this.bundleSearching = false
                    this.bundleOpen = true
                }
            }
        },
        toggleBundleFullList() {
            if (this.bundleQuery.trim() || this.selectedBundle) return
            if (this.bundleOpen && this.bundleBrowsingAll && !this.bundleSearching) {
                this.bundleOpen = false
                return
            }
            this.fetchAllBundles()
        },
        async fetchAllBundles() {
            if (this.bundleQuery.trim() || this.selectedBundle) return
            if (this._bundleTimer) clearTimeout(this._bundleTimer)
            this._bundleSearchSeq += 1
            const seq = this._bundleSearchSeq
            this.bundleTouched = true
            this.bundleBrowsingAll = true
            this.bundleSearching = true
            this.bundleMatches = []
            this.bundleOpen = true
            try {
                const res = await this.$http.post(this.resources.BundleIDs.getBundleList)
                if (seq !== this._bundleSearchSeq) return
                const rows = (res && res.body && res.body.data) || []
                this.bundleMatches = rows
            } catch (err) {
                console.error('getBundleList', err)
                if (seq !== this._bundleSearchSeq) return
                this.bundleMatches = []
            } finally {
                if (seq === this._bundleSearchSeq) {
                    this.bundleSearching = false
                    this.bundleOpen = true
                }
            }
        },
        pickBundle(b) {
            if (!b || !b.bundle_id) return
            if (this._bundleTimer) clearTimeout(this._bundleTimer)
            this._bundleSearchSeq += 1
            this.bundleId = b.bundle_id
            this.selectedBundleCache = b
            this.bundleQuery = formatBundleInputLabel(b) || b.bundle_id
            this.bundleMatches = [b]
            this.bundleSearching = false
            this.bundleBrowsingAll = false
            this.bundleOpen = false
            this.applyBundleOs(b)
            this.emitFormSync()
        },
        clearBundle() {
            if (this._bundleTimer) clearTimeout(this._bundleTimer)
            this._bundleSearchSeq += 1
            this.bundleQuery = ''
            this.bundleTouched = false
            this.bundleId = ''
            this.selectedBundleCache = null
            this.bundleMatches = []
            this.bundleSearching = false
            this.bundleBrowsingAll = false
            this.emitFormSync()
        },
        async hydrateBundle(bundleId) {
            const q = String(bundleId || '').trim()
            if (!q) return
            try {
                const res = await this.$http.post(this.resources.BundleIDs.searchBundles, { q })
                const rows = (res && res.body && res.body.data) || []
                const hit = rows.find((b) => b.bundle_id === q) || null
                if (hit) this.pickBundle(hit)
                else this.bundleQuery = q
            } catch (err) {
                this.bundleQuery = q
            }
        },
        applyLinks() {
            if (!this.canApply) return
            this.$emit('apply', {
                links: this.results.map((r) => ({
                    account: `${r.name} (${r.pid})`,
                    pid: r.pid,
                    click: r.cta,
                    impression: r.vta || null,
                })),
            })
        },
        isEditingUrl(key) {
            return !!this.editingUrls[key]
        },
        toggleEditUrl(key) {
            this.$set(this.editingUrls, key, !this.editingUrls[key])
        },
        onEditedUrl(row, field, value) {
            const next = stripWhitespaceIfPresent(value)
            if (next.stripped) this.notifyError('URL should not contain white spaces')
            this.$set(row, field, next.value)
        },
        networkPillTitle(network) {
            if (network.subFields) {
                const s = network.subFields
                return `${network.pid} — af_adset=${s.af_adset || ''} af_adset_id=${s.af_adset_id || ''} af_ad=${s.af_ad || ''} af_ad_id=${s.af_ad_id || ''}`
            }
            return `${network.pid} — codes: ${network.codes.join(', ')}`
        },
        toggleNet(pid) {
            const idx = this.selected.indexOf(pid)
            if (idx === -1) {
                this.selected.push(pid)
            } else {
                this.selected.splice(idx, 1)
            }
            this.emitFormSync()
        },
        resetAdvanced() {
            this.advanced = { ...DEFAULT_ADVANCED }
        },
        normalizeBundleId() {
            let id = String(this.bundleId || this.bundleQuery || '').trim()
            if (this.bundleId) id = String(this.bundleId).trim()
            if (this.os === 'ios' && id.startsWith('id')) {
                id = id.substr(2)
            }
            return id
        },
        hasWhitespace(value) {
            return /\s/.test(value == null ? '' : String(value))
        },
        findWhitespaceParams() {
            const checks = [
                { label: 'Bundle ID', value: this.normalizeBundleId() },
                { label: 'Campaign Name', value: this.campaignName },
                { label: 'AF Prt', value: this.resolvedAgency },
                { label: 'Additional Params', value: this.additionalParams },
            ]
            if (this.isRetargeting === 'yes') {
                checks.push(
                    { label: 'af_reengagement_window', value: this.reengagementWindow },
                    { label: 'af_inactivity_window', value: this.inactivityWindow },
                )
            }
            this.advancedFields.forEach((field) => {
                checks.push({ label: field.label, value: this.advanced[field.key] })
            })
            return checks.filter((c) => this.hasWhitespace(c.value)).map((c) => c.label)
        },
        generate() {
            const bundleId = this.normalizeBundleId()
            const be = validateBundle(this.os, bundleId)
            if (be) {
                this.error = be
                this.$emit('can-apply', false)
                return
            }
            if (this.selected.length === 0) {
                this.error = 'Select at least one ad network / account.'
                this.$emit('can-apply', false)
                return
            }
            if (!this.campaignName.trim()) {
                this.error = 'Campaign name (c) is required.'
                this.$emit('can-apply', false)
                return
            }
            const whitespaceParams = this.findWhitespaceParams()
            if (whitespaceParams.length) {
                this.notifyError(`Error - The following fields contain spaces: ${whitespaceParams.join(', ')}.`)
                this.$emit('can-apply', false)
                return
            }

            this.error = ''
            this.editingUrls = {}
            const invalidPid = this.selected.find((pid) => {
                const net = this.allNetworks.find((n) => n.pid === pid)
                return !net || !net.subFields
            })
            if (invalidPid) {
                this.error = `Incomplete sub configuration for pid ${invalidPid}.`
                this.$emit('can-apply', false)
                return
            }

            const channelOverride = this.advanced.af_channel.trim()
            const out = this.selected.map((pid) => {
                const net = this.allNetworks.find((n) => n.pid === pid)
                const channel = channelOverride || pickChannel()
                const subFields = net.subFields || null
                let adset
                let ad
                if (!subFields) {
                    ({ adset, ad } = buildAdsetAd(net.codes))
                }
                const common = {
                    os: this.os,
                    bundleId,
                    agency: this.resolvedAgency,
                    pid,
                    channel,
                    campaignName: this.campaignName,
                    costModel: this.costModel,
                    isRetargeting: this.isRetargeting,
                    additionalParams: this.additionalParams,
                    subFields,
                    adset,
                    ad,
                    advanced: this.advanced,
                    reengagementWindow: this.reengagementWindow,
                    inactivityWindow: this.inactivityWindow,
                    originOverride: this.originOverride,
                }
                return {
                    name: net.name,
                    pid,
                    channel,
                    subFields,
                    adset,
                    ad,
                    cta: buildUrl({ ...common, isVTA: false }),
                    vta: this.withVTA ? buildUrl({ ...common, isVTA: true }) : null,
                }
            })
            this.results = out
            this.$emit('can-apply', true)
        },
        async copyText(text, key) {
            try {
                await navigator.clipboard.writeText(text)
            } catch {
                const ta = document.createElement('textarea')
                ta.value = text
                document.body.appendChild(ta)
                ta.select()
                document.execCommand('copy')
                document.body.removeChild(ta)
            }
            this.copiedKey = key
            if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
            this.copiedTimeout = setTimeout(() => {
                this.copiedKey = null
            }, 1400)
        },
    },
}
</script>

<style lang="css" scoped>
.af-generator {
    min-height: 100%;
    padding: 24px;
    color: #0f172a;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
    text-align: left;
}

.af-generator-embedded {
    min-height: 0;
    padding: 0;
}

.af-loading {
    color: #475569;
    padding: 24px 0;
}

.af-header {
    margin-bottom: 18px;
}

.af-header h1 {
    font-size: 2rem;
    line-height: 1.1;
    font-weight: 800;
    margin: 0;
}

.af-header p {
    font-size: 0.95rem;
    color: rgba(15, 23, 42, 0.7);
    margin: 6px 0 0;
}

.af-card {
    border-radius: 20px;
    padding: 20px 24px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(15, 23, 42, 0.1);
    box-shadow: 0 18px 60px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(10px);
    text-align: left;
}


.af-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin-block: 10px;
}

.af-grid-2 {
    display: grid;
    grid-template-columns: auto auto;
    gap: 20px;
    justify-content: start;
}

.af-grid-3 {
    display: grid;
    grid-template-columns: auto auto auto;
    gap: 20px;
    justify-content: start;
}

@media (max-width: 640px) {
    .af-grid-2,
    .af-grid-3 {
        grid-template-columns: 1fr;
    }
}

.af-seg-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-start;
}

.af-seg {
    padding: 8px 12px;
    font-size: 0.875rem;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.65);
    background: rgba(255, 255, 255, 0.92);
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.af-seg:hover {
    border-color: rgba(14, 165, 233, 0.5);
}

.af-seg.active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 10px 18px rgba(14, 165, 233, 0.22);
}

.af-field {
    margin-top: 0;
    text-align: left;
}

.af-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
}

.af-pill {
    padding: 6px 12px;
    font-size: 0.75rem;
    border-radius: 9999px;
    border: 1px solid rgba(148, 163, 184, 0.6);
    background: rgba(255, 255, 255, 0.92);
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.af-pill:hover {
    border-color: rgba(14, 165, 233, 0.5);
}

.af-pill.active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 16px rgba(14, 165, 233, 0.22);
}

.af-pill-other {
    color: #475569;
}

.af-pill-pid {
    opacity: 0.6;
    font-family: ui-monospace, monospace;
}

.af-subsection-label {
    margin: 12px 0 8px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
}

.af-toggle-more {
    display: block;
    margin-top: 8px;
    font-size: 0.75rem;
    color: #0284c7;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.af-toggle-more:hover {
    color: #0369a1;
}

.af-hint {
    font-size: 0.75rem;
    color: #64748b;
    margin: 4px 0 0;
}

.af-input {
    display: block;
    width: 100%;
    max-width: 420px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(148, 163, 184, 0.55);
    color: #0f172a;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
    text-align: left;
}

.af-input:focus {
    border-color: rgba(14, 165, 233, 0.85);
    box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

.af-input-mono {
    font-family: ui-monospace, monospace;
}

.af-input-error {
    border-color: #dc2626;
}

.af-combo {
    position: relative;
    max-width: 420px;
    z-index: 1;
}

.af-combo.is-open {
    z-index: 20;
}

.af-combo-input-wrap {
    position: relative;
}

.af-combo-input-wrap .af-input {
    width: 100%;
    max-width: none;
    padding-right: 64px;
}

.af-combo-clear,
.af-combo-chevron {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: none;
    color: #64748b;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    font-size: 1rem;
}

.af-combo-clear {
    right: 28px;
}

.af-combo-clear:hover,
.af-combo-chevron:hover:not(:disabled) {
    color: #0f172a;
}

.af-combo-chevron {
    right: 8px;
}

.af-combo-chevron:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
}

.af-combo-dropdown {
    position: absolute;
    z-index: 30;
    margin: 4px 0 0;
    width: 100%;
    max-height: 240px;
    overflow: auto;
    list-style: none;
    padding: 4px 0;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.55);
    background: #fff;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.af-combo-dropdown li button {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    border: none;
    background: none;
    padding: 8px 12px;
    cursor: pointer;
    text-align: left;
}

.af-combo-dropdown li button:hover,
.af-combo-dropdown li button.active {
    background: #f1f5f9;
}

.af-combo-app {
    display: block;
    font-size: 0.875rem;
    color: #334155;
}

.af-combo-id {
    display: block;
    font-size: 0.75rem;
    color: #94a3b8;
    font-family: ui-monospace, monospace;
}

.af-combo-meta {
    flex-shrink: 0;
    font-size: 0.75rem;
    color: #64748b;
}

.af-combo-status {
    padding: 8px 12px;
    font-size: 0.75rem;
    color: #64748b;
}

.af-agency-row {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 640px;
}

.af-agency-select {
    flex: 0 0 220px;
    max-width: 220px;
}

.af-agency-row .af-input:not(.af-agency-select) {
    flex: 1;
    min-width: 0;
    max-width: none;
}

.af-error {
    font-size: 0.75rem;
    color: #dc2626;
    margin: 4px 0 0;
}

.af-error-block {
    font-size: 0.875rem;
}

.af-retarget-windows {
    margin-top: 8px;
}

.af-advanced {
    margin: 16px 0 12px;
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 12px;
    background: rgba(248, 250, 252, 0.7);
    overflow: hidden;
}

.af-advanced-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
}

.af-advanced-toggle {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    text-align: left;
}

.af-advanced-toggle:hover {
    color: #0284c7;
}

.af-advanced-reset {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.75rem;
    color: #0284c7;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.af-advanced-reset:hover {
    color: #0369a1;
}

.af-advanced-body {
    padding: 0 14px 14px;
    border-top: 1px solid rgba(148, 163, 184, 0.35);
}

.af-advanced-body .af-hint {
    margin: 10px 0 8px;
}

.af-advanced-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
}

@media (max-width: 640px) {
    .af-advanced-grid {
        grid-template-columns: 1fr;
    }
}

.af-advanced-grid .af-input {
    max-width: none;
}

.af-generate-btn {
    display: inline-block;
    width: auto;
    padding: 12px 24px;
    border-radius: 10px;
    background: linear-gradient(135deg, #10b981, #22c55e);
    border: none;
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
}

.af-generate-btn:hover {
    filter: brightness(1.03);
}

.af-results {
    margin-top: 24px;
}

.af-results > * + * {
    margin-top: 16px;
}

.af-results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.af-results-header h2 {
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
}

.af-results-actions {
    display: flex;
    gap: 8px;
}

.af-result-card {
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.af-result-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.af-result-name {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
}

.af-badge {
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 9999px;
    background: rgba(248, 250, 252, 1);
    border: 1px solid rgba(148, 163, 184, 0.5);
    color: #475569;
    font-family: ui-monospace, monospace;
}

.af-link-block + .af-link-block {
    margin-top: 12px;
}

.af-link-label {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 4px;
}

.af-link-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.af-link-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
}

.af-link-code {
    flex: 1;
    min-width: 0;
    font-size: 0.75rem;
    color: #1e293b;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.5);
    border-radius: 8px;
    padding: 10px;
    word-break: break-all;
    overflow-wrap: anywhere;
    font-family: ui-monospace, monospace;
    line-height: 1.6;
    white-space: pre-wrap;
}

.af-link-code-edit {
    min-height: 96px;
    resize: vertical;
    display: block;
    box-sizing: border-box;
    background: #fff;
    border-color: #2563eb;
    outline: none;
}

.af-seg,
.af-pill,
.af-copy-btn {
    text-align: left;
}

.af-copy-btn {
    flex-shrink: 0;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 10px;
    background: linear-gradient(135deg, #64748b, #475569);
    border: none;
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;
}

.af-copy-btn:hover {
    filter: brightness(1.04);
}
</style>
